import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { login, getRegistrationStatus } from '../api';
import toast from 'react-hot-toast';
import { Zap, ShieldCheck } from 'lucide-react';
import Button from '../components/Button';

export default function Login() {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [twofa, setTwofa] = useState(false); // show the 2FA code step
  const [code, setCode] = useState('');

  useEffect(() => {
    getRegistrationStatus().then(r => setCanRegister(!!r.allowed)).catch(() => {});
  }, []);

  const handle = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(form.email, form.password, twofa ? code : undefined);
      if (res.twofa_required) {
        setTwofa(true);          // password ok — ask for the authenticator code
        return;
      }
      loginUser(res.token, res.user);
      navigate('/');
    } catch (err) {
      if (err?.twofa_required) setTwofa(true);
      toast.error(err?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <div style={{ display:'flex', alignItems:'center', gap:11, marginBottom:26 }}>
          <div style={{ width:42, height:42, background:'var(--grad)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 20px var(--accent-glow)' }}>
            <Zap size={20} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--text)' }}>WP Manager</div>
            <div style={{ fontSize:11, color:'var(--text3)' }}>WordPress dashboard</div>
          </div>
        </div>

        {twofa ? (
          <>
            <h2>Two-factor authentication</h2>
            <p>Enter the 6-digit code from your authenticator app (or a backup code).</p>
            <form onSubmit={handle}>
              <div className="field">
                <label style={{ display:'flex', alignItems:'center', gap:6 }}><ShieldCheck size={13} color="var(--accent)"/> Authentication code</label>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus
                  placeholder="123456" value={code}
                  onChange={e=>setCode(e.target.value.replace(/[^0-9a-fA-F-]/g,''))}
                  style={{ letterSpacing:'3px', textAlign:'center', fontSize:16 }} required />
              </div>
              <Button type="submit" className="btn primary" style={{ width:'100%', justifyContent:'center', marginTop:4 }} loading={loading}>
                {loading ? 'Verifying...' : 'Verify & sign in'}
              </Button>
            </form>
            <p style={{ marginTop:14, textAlign:'center', fontSize:12 }}>
              <span className="auth-link" style={{ cursor:'pointer' }} onClick={()=>{ setTwofa(false); setCode(''); }}>← Back</span>
            </p>
          </>
        ) : (
          <>
            <h2>Sign in</h2>
            <p>Manage all your WordPress sites from one place</p>

            <form onSubmit={handle}>
              <div className="field">
                <label>Email</label>
                <input type="email" placeholder="you@example.com" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required />
              </div>
              <div className="field">
                <label>Password</label>
                <input type="password" placeholder="••••••••" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required />
              </div>
              <Button type="submit" className="btn primary" style={{ width:'100%', justifyContent:'center', marginTop:4 }} loading={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <p style={{ marginTop:14, textAlign:'center', fontSize:12 }}>
              <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
            </p>

            {canRegister && (
              <p style={{ marginTop:8, textAlign:'center', fontSize:12, color:'var(--text3)' }}>
                No account? <Link to="/register" className="auth-link">Create one</Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
